import { definedMap, panic } from "@glideapps/ts-necessities";

import { handlerForActionKind } from ".";
import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { setColumnInRowForEditComponent } from "../builder-utils";
import {
    ActionsRecord,
    AllowedDataEdits,
    ComponentBuilder,
    ComponentConfiguratorContext,
    EditedColumns,
    makeEmptyMutableAllowedDataEdits,
    mergeAllowedDataEdits,
    PropertyDescriptor,
} from "../components";
import { getPrimitiveActionKinds } from "../components/primitive";
import { ColumnPropertyFlag, ColumnPropertyHandler, SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { getActionProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    getPrimitiveNonHiddenColumns,
    hasInputContext,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";

interface LiveScanLinearBarcodeActionDescription extends ActionDescription {
    readonly output: PropertyDescription;
    readonly onScanAction: PropertyDescription | undefined;
    readonly closeAfterScanning: PropertyDescription | undefined;
}

const columnPropertyHandler = new ColumnPropertyHandler(
    "output",
    "Column",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["item", "sku", "upc", "ean", "barcode"],
    getPrimitiveNonHiddenColumns,
    "string",
    PropertySection.Data
);
const closeAfterScanningPropertyHandler = new SwitchPropertyHandler(
    { closeAfterScanning: true },
    "Automatically close after scanning",
    PropertySection.ScanningOptions
);

export class LiveScanLinearBarcodeActionHandler extends BaseActionHandler<LiveScanLinearBarcodeActionDescription> {
    public readonly kind = ActionKind.LiveScanLinearBarcode;

    public readonly iconName = "09-06-barcode-scan";
    public get handlesSequencing(): boolean {
        return true;
    }

    public getDescriptor(
        _desc: LiveScanLinearBarcodeActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        const actionKinds: readonly ActionKind[] = getPrimitiveActionKinds(
            hasInputContext(mutatingScreenKind),
            ccc.userProfileTableInfo !== undefined
        );

        const properties: PropertyDescriptor[] = ccc.userFeatures.massScanBarcodes
            ? [
                  columnPropertyHandler,
                  {
                      kind: PropertyKind.Action,
                      property: { name: "onScanAction" },
                      label: "Scan action ",
                      required: false,
                      kinds: actionKinds,
                      section: PropertySection.ScanAction,
                  },
                  closeAfterScanningPropertyHandler,
              ]
            : [columnPropertyHandler];

        return {
            name: "Scan Barcode",
            needsScreenContext: true,
            isLegacy: !getFeatureSetting("liveScanLinearBarcode") && !ccc.userFeatures.previewLiveLinearBarcodeScanner,
            properties,
        };
    }

    public getActions(desc: LiveScanLinearBarcodeActionDescription): ActionsRecord {
        const onScan = getActionProperty(desc.onScanAction);
        return { actions: [], onScanAction: definedMap(onScan, a => [a]) ?? [] };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: LiveScanLinearBarcodeActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _detailScreenTitle: SerializedComponent | undefined,
        _itemID: SerializedComponent | undefined,
        onSuccess: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Scan Barcode action");
        }

        const outputProperty = columnPropertyHandler.getColumnName(desc);
        if (outputProperty === undefined) return undefined;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            outputProperty,
            b.getProperty(outputProperty, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            screenContext,
            { debounce: false, withDiode: false }
        );
        if (setColumnsResult === undefined) return undefined;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const actionSequence: SerializedComponent[] = [];
        if (action !== undefined) {
            actionSequence.push(action);
        }
        if (onSuccess !== undefined) {
            actionSequence.push(onSuccess);
        }

        const onScanDesc = getActionProperty(desc.onScanAction);
        const onScanAction = definedMap(onScanDesc, a =>
            builder.makeSequenceFromActions([a], tables, screenContext, outputContext, mutatingScreenKind, false)
        );
        if (onScanAction !== undefined) {
            actionSequence.push(onScanAction);
        }
        if (closeAfterScanningPropertyHandler.getSwitch(desc)) {
            actionSequence.push(b.popModalScreen);
        }

        return b.let(
            lets,
            b.if(
                isEnabled ?? b.constant(true),
                builder.makePushLiveLinearCodeScannerScreen(valueToModify, b.sequence(actionSequence))
            )
        );
    }

    public getEditedColumns(
        desc: LiveScanLinearBarcodeActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): EditedColumns | undefined {
        const editedColumns = super.getEditedColumns(desc, tables, ccc, mutatingScreenKind);
        if (editedColumns === undefined) return undefined;

        const onScanDesc = getActionProperty(desc.onScanAction);
        if (onScanDesc === undefined) return editedColumns;

        const handler = handlerForActionKind(onScanDesc.kind);
        if (handler === undefined) return editedColumns;

        const onScanEdited = handler.getEditedColumns(onScanDesc, tables, ccc, mutatingScreenKind);
        if (onScanEdited === undefined) return editedColumns;

        return [...editedColumns, ...onScanEdited];
    }

    public getAllowedDataEdits(
        desc: LiveScanLinearBarcodeActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): AllowedDataEdits | undefined {
        const dataEdits = makeEmptyMutableAllowedDataEdits();

        const regular = super.getAllowedDataEdits(desc, tables, ccc, mutatingScreenKind);
        mergeAllowedDataEdits(dataEdits, regular);

        const onScanDesc = getActionProperty(desc.onScanAction);
        if (onScanDesc === undefined) return dataEdits;

        const handler = handlerForActionKind(onScanDesc.kind);
        if (handler === undefined) return dataEdits;

        const nested = handler.getAllowedDataEdits(onScanDesc, tables, ccc, mutatingScreenKind);
        mergeAllowedDataEdits(dataEdits, nested);

        return dataEdits;
    }

    public getTokenizedDescription(
        desc: LiveScanLinearBarcodeActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const token = tokenForProperty(desc.output, tables, ccc);
        if (token === undefined) return undefined;
        return [{ kind: "string", value: "Scan into" }, token];
    }
}
